import { Dynamic } from "solid-js/web";
import { cn } from "@mealplan/shared";

interface LogoProps {
  size?: "sm" | "lg";
  class?: string;
  /**
   * Heading semantics for the `lg` wordmark. Default "h1": the logo is the page
   * heading. Pass "p" when a sibling already owns the page h1 (e.g. the auth
   * card carries the h1 and the brand surface is decorative).
   */
  as?: "h1" | "p";
}

/**
 * Mealplan's own brand: a plate seen from above with cutlery (fork +
 * spoon) and a sprig of fresh herbs, atop a subtle "meal calendar".
 * DISTINCTIVE product mark (NOT the template's generic CheckSquare).
 *
 * REAL GOTCHA (confirmed): drawing the mark with `stroke-accent`/`fill-accent` or
 * any brand color on a dim surface makes it INVISIBLE in
 * several themes (e.g. amber/slate/indigo of the ~16 seeds), because each seed
 * overrides `--accent` to an arbitrary color that can clash with the background.
 *
 * FIX: the mark is drawn with `currentColor`, inherited from a SOLID chip
 * `bg-accent` + `text-accent-foreground`. That pair has GUARANTEED contrast
 * by the theme engine (it is the same pair used by primary buttons), so
 * the mark is readable in EVERY theme (base light/dark/initialization + seeds).
 * The wordmark uses `text-foreground` (contrast with background, validated per theme).
 */
function LogoMark(props: { class?: string }) {
  return (
    <span
      class={cn(
        "grid shrink-0 place-items-center rounded-2xl bg-accent text-accent-foreground shadow-sm ring-1 ring-black/5",
        props.class,
      )}
    >
      <svg
        viewBox="0 0 32 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        class="h-[68%] w-[68%]"
      >
        {/* Calendario de comidas (sutil, al fondo) */}
        <rect
          x="5"
          y="6"
          width="22"
          height="21"
          rx="3.5"
          stroke="currentColor"
          stroke-width="1.6"
          opacity="0.45"
        />
        <path d="M5 11.5h22" stroke="currentColor" stroke-width="1.6" opacity="0.45" />
        <path
          d="M11 4.5v3.5M21 4.5v3.5"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linecap="round"
          opacity="0.45"
        />
        {/* Plato */}
        <circle cx="16" cy="19" r="6.4" stroke="currentColor" stroke-width="1.7" />
        <circle cx="16" cy="19" r="3" stroke="currentColor" stroke-width="1.3" opacity="0.55" />
        {/* Tenedor */}
        <g stroke="currentColor" stroke-width="1.5" stroke-linecap="round">
          <path d="M10 14v2.6" />
          <path d="M12 14v2.6" />
          <path d="M11 16.6V25" />
        </g>
        {/* Cuchara */}
        <path
          d="M21 14.4c1.5 0 2.4 1.2 2.4 2.6 0 1.2-.8 2.1-2 2.3M21.4 19.3 21 25"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </span>
  );
}

export function Logo(props: LogoProps) {
  const size = () => props.size ?? "sm";

  return (
    <div
      class={cn(
        "flex items-center gap-3 select-none",
        size() === "lg" && "flex-col gap-4 text-center",
        props.class,
      )}
    >
      {size() === "sm" ? (
        <>
          <LogoMark class="h-9 w-9" />
          <span class="font-serif text-2xl font-semibold tracking-tight text-foreground">
            Meal<span class="text-accent">plan</span>
          </span>
        </>
      ) : (
        <>
          <LogoMark class="mb-1 h-16 w-16 transition-transform duration-300 hover:scale-105 active:scale-95" />
          <Dynamic
            component={props.as ?? "h1"}
            class="font-serif text-3xl font-semibold tracking-tight text-foreground"
          >
            Meal<span class="text-accent">plan</span>
          </Dynamic>
        </>
      )}
    </div>
  );
}
